import { Icon, Spacer, Tooltip } from "@zapier/design-system";
import { Modal } from "blocks/components/Modal";
import { Text } from "blocks/components/Text";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Button, TextArea } from "blocks/components";
import { usePrevious } from "lib/hooks";
import { cn } from "utils/cn";

type Feedback = {
  rating: number;
  comment?: string;
};

type BotFeedbackProps = {
  onFeedback: SubmitHandler<Feedback>;
};

export function BotFeedback({ onFeedback }: BotFeedbackProps) {
  const formRef = useRef<HTMLFormElement>(null);

  const [showModal, setShowModal] = useState(false);
  const prevShowModal = usePrevious(showModal);

  const { control, handleSubmit, watch, setValue } = useForm<Feedback>({
    defaultValues: {
      rating: 0,
      comment: "",
    },
  });

  const rating = watch("rating");

  const handleFeedback = useCallback<SubmitHandler<Feedback>>(
    (data) => {
      setShowModal(false);
      return onFeedback(data);
    },
    [onFeedback]
  );

  useEffect(() => {
    setShowModal(rating !== 0);
  }, [rating]);

  useEffect(() => {
    if (prevShowModal && !showModal && formRef.current) {
      if (formRef.current.requestSubmit) {
        formRef.current.requestSubmit();
      } else {
        formRef.current.submit();
      }
    }
  }, [prevShowModal, showModal]);

  return (
    <form onSubmit={handleSubmit(handleFeedback)} ref={formRef}>
      <FeedbackButtons>
        {rating > -1 && (
          <FeedbackButton
            id="positive"
            disabled={rating !== 0}
            onClick={() => setValue("rating", 1)}
            tooltip="This response is helpful"
          >
            <Icon name="ratingThumbsUp" size={20} />
          </FeedbackButton>
        )}
        {rating < 1 && (
          <FeedbackButton
            id="negative"
            disabled={rating !== 0}
            onClick={() => setValue("rating", -1)}
            tooltip="This response is not helpful"
          >
            <Icon name="ratingThumbsDown" size={20} />
          </FeedbackButton>
        )}
      </FeedbackButtons>
      {showModal && (
        <Modal onClosed={() => setShowModal(false)}>
          <StyledModalContent>
            <Text type="modalTitle">Rating feedback</Text>
            <Spacer height={20} />
            <Controller
              control={control}
              name="comment"
              render={({ field }) => (
                <TextArea
                  {...field}
                  placeholder={
                    rating === 1
                      ? "What do you like about the response?"
                      : "What do you dislike about the response?"
                  }
                />
              )}
            />
            <Spacer height={30} />
            <Button type="button" onClick={() => setShowModal(false)}>
              Submit feedback
            </Button>
          </StyledModalContent>
        </Modal>
      )}
    </form>
  );
}

type FeedbackButtonProps = Omit<
  React.ComponentPropsWithoutRef<"button">,
  "id"
> & {
  id: string;
  tooltip: string;
};

function FeedbackButton({ tooltip, ...props }: FeedbackButtonProps) {
  const [showTooltip, setShowTooltip] = useState(props.disabled ?? false);
  const ariaDescribedById = `${props.id}-desc`;
  return (
    <StyledFeedbackButtonWrapper
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
    >
      <StyledFeedbackButton {...props} aria-describedby={ariaDescribedById} />
      <StyledTooltipWrapper $showTooltip={!props.disabled && showTooltip}>
        <Tooltip position="northeast" id={ariaDescribedById}>
          {tooltip}
        </Tooltip>
      </StyledTooltipWrapper>
    </StyledFeedbackButtonWrapper>
  );
}

const FeedbackButtons: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div {...props} className={cn("flex items-center gap-1", props.className)} />
);

const StyledFeedbackButtonWrapper: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div {...props} className={cn("relative inline-block", props.className)} />
);

const StyledFeedbackButton: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"button">
> = (props) => (
  <button
    {...props}
    className={cn(
      "m-0 h-6 w-6 cursor-pointer rounded border-0 bg-zi-lightPrimary p-0.5 text-zi-primary/50 transition-colors focus-within:text-zi-primary focus-within:outline-none focus-within:ring-1 focus-within:ring-zi-primary focus-within:ring-offset-2 focus-within:ring-offset-zi-lightPrimary hover:bg-zi-primary/20 hover:text-zi-primary hover:focus-within:ring-offset-zi-primary/20 disabled:cursor-not-allowed disabled:text-zi-primary hover:disabled:bg-zi-lightPrimary",
      props.className
    )}
  />
);

const StyledTooltipWrapper: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div"> & {
    $showTooltip: boolean;
  }
> = ({ $showTooltip, ...props }) => (
  <div
    {...props}
    className={cn(
      "transition-opacity",
      {
        "opacity-0": !$showTooltip,
        "opacity-100": $showTooltip,
      },
      props.className
    )}
  />
);

const StyledModalContent: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div
    {...props}
    className={cn(
      "flex flex-col items-stretch [&>button]:w-auto [&>button]:self-start",
      props.className
    )}
  />
);
